import { Suspense, useEffect } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { PreloadedQuery, useFragment, usePreloadedQuery, useQueryLoader } from "react-relay"
import { Phone } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { useIsFeatureEnabled } from "@/hooks/useIsFeatureEnabled"
import { broadcastCallStatus } from "@/lib/call-status-broadcast"
import { useMedicalRecordDialogStore } from "@/modules/patients/detail/store/useCloseMedicalRecordDialogStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { chPatientByIdQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientByIdQuery.graphql"
import { dePatientByIdQuery$data } from "@/modules/patients/services/de/__generated__/dePatientByIdQuery.graphql"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { chCallButtonQuery } from "@/modules/telephony/call-button/ch/__generated__/chCallButtonQuery.graphql"
import { chCallButtonQuery as chCallButtonQueryNode } from "@/modules/telephony/call-button/ch/chCallButtonQuery"
import { deCallButtonQuery } from "@/modules/telephony/call-button/de/__generated__/deCallButtonQuery.graphql"
import { deCallButtonQuery as deCallButtonQueryNode } from "@/modules/telephony/call-button/de/deCallButtonQuery"
import { useGenesysTelephony } from "@/modules/telephony/hooks/useGenesysTelephony"
import { useCallPatientMutation } from "@/modules/telephony/services/useCallPatientMutation"
import { useStartOutboundCallMutation } from "@/modules/telephony/services/useStartOutboundCallMutation"
import { usePhoneCallTranscriptionStore } from "@/stores/usePhoneCallTranscriptionStore"
import { Country, Language } from "@/types/__generated__/union-types"

type OutBoundCallButtonProps = {
  appointmentId: string
  phoneNumber: string
  patientGlobalId: string
  progressNoteGlobalId: string
  progressNoteId: number
  language: Language
  patient: chPatientByIdQuery$data | dePatientByIdQuery$data
}

const OutBoundCallButton = ({
  appointmentId,
  phoneNumber,
  patientGlobalId,
  progressNoteGlobalId,
  progressNoteId,
  language,
  patient,
}: OutBoundCallButtonProps) => {
  const country = useCountryParam()
  const [queryReference, loadQuery] = useQueryLoader<chCallButtonQuery | deCallButtonQuery>(
    country === "CH" ? chCallButtonQueryNode : deCallButtonQueryNode,
  )
  const patientData = useFragment<PatientFragment$key>(PatientFragment, patient.patientById)

  useEffect(() => {
    loadQuery({ id: patientGlobalId }, { fetchPolicy: "store-or-network" })
  }, [patientGlobalId, loadQuery])

  if (!patientData) return <CallButton disabled />

  const patientName = `${patientData.firstName} ${patientData.lastName}`

  return (
    <ErrorBoundary fallback={<CallButton disabled />}>
      <Suspense fallback={<CallButton disabled />}>
        {queryReference && (
          <OutBoundCallButtonData
            appointmentId={appointmentId}
            phoneNumber={phoneNumber}
            patientId={patientData.patientId}
            patientName={patientName}
            progressNoteGlobalId={progressNoteGlobalId}
            progressNoteId={progressNoteId}
            language={language}
            country={country}
            queryReference={queryReference}
          />
        )}
      </Suspense>
    </ErrorBoundary>
  )
}

type OutBoundCallButtonDataProps = {
  appointmentId: string
  phoneNumber: string
  patientId: string
  patientName: string
  progressNoteGlobalId: string
  progressNoteId: number
  language: Language
  country: Country
  queryReference: PreloadedQuery<chCallButtonQuery | deCallButtonQuery>
}

const OutBoundCallButtonData = ({
  appointmentId,
  phoneNumber,
  patientId,
  patientName,
  progressNoteGlobalId,
  progressNoteId,
  language,
  country,
  queryReference,
}: OutBoundCallButtonDataProps) => {
  const setShouldIncreaseMissedCall = useMedicalRecordDialogStore((state) => state.setShouldIncreaseMissedCall)
  const {
    callLineInformation: { callLine, isRecorded },
  } = usePreloadedQuery<chCallButtonQuery | deCallButtonQuery>(
    country === "CH" ? chCallButtonQueryNode : deCallButtonQueryNode,
    queryReference,
  )
  const [startOutboundCall] = useStartOutboundCallMutation()
  const [callPatient, isCallPatientInFlight] = useCallPatientMutation()
  const isSpeechToTextEnabled = useIsFeatureEnabled("SPEECH_TO_TEXT")
  const { isGenesysLoginInFlight, openGenesysLoginWindow, runViaGenesysIfEnabled } = useGenesysTelephony()
  const startRecording = usePhoneCallTranscriptionStore((state) => state.startRecording)

  const { t } = useTranslation()

  const isRecordedText = isRecorded
    ? t("episodeOfCare.outBoundCallButton.recordedCall")
    : t("episodeOfCare.outBoundCallButton.notRecordedCall")

  const startAudioCall = () => {
    const callLink = `nimbus-call:${phoneNumber}:${callLine}`
    window.open(callLink)
  }

  const startTranscriptionIfEnabled = () => {
    if (!isSpeechToTextEnabled) return

    broadcastCallStatus({ channel: "phone" })
    startRecording(appointmentId, progressNoteGlobalId, patientName, language)
  }

  const handleCallStarted = () => {
    setShouldIncreaseMissedCall(true)
    startTranscriptionIfEnabled()
  }

  const callPatientWithGenesys = () =>
    callPatient(
      { patientId, progressNoteId, phoneNumber, isRecordingAllowed: isRecorded, country },
      handleCallStarted,
      () => openGenesysLoginWindow(callPatientWithGenesys),
    )

  const callPatientWithLuware = () => {
    startAudioCall()
    startOutboundCall(appointmentId, isRecorded, phoneNumber)
    handleCallStarted()
  }

  const handleClick = () => runViaGenesysIfEnabled(callPatientWithGenesys, callPatientWithLuware)

  return (
    <CallButton
      handleClick={handleClick}
      disabled={isGenesysLoginInFlight || isCallPatientInFlight}
      title={isRecordedText}
    />
  )
}

const CallButton = ({
  handleClick,
  disabled,
  title,
}: {
  handleClick?: VoidFunction
  disabled?: boolean
  title?: string
}) => {
  return (
    <Button
      title={title}
      disabled={disabled}
      variant="circle"
      className="bg-primary hover:bg-primary-hovered h-10 w-10 shrink-0 p-1"
      onClick={handleClick}
      data-testid="call-video-btn">
      <Phone size={24} className="text-on-primary!" />
    </Button>
  )
}

export { OutBoundCallButton }
